import { Link } from 'react-router-dom';
import {
  FaBed,
  FaCar,
  FaHeadphones,
  FaPercent,
  FaShip,
  FaShieldAlt,
  FaStar,
  FaSuitcase,
} from 'react-icons/fa';
import CarSearchWidget, { scrollToTravelSection } from '../components/home/CarSearchWidget';
import SectionHeader from '../components/home/SectionHeader';
import EnquiryForm from '../components/EnquiryForm';
import {
  CRUISE_DEALS,
  EXCLUSIVE_DEALS,
  FEATURES,
  HERO_IMAGE,
  HOTEL_PICKS,
  PACKAGE_DEALS,
  REVIEWS,
  TOP_LOCATIONS,
  TRAVEL_SERVICES,
} from '../data/homeData';
import { SITE_NAME } from '../constants/site';
import ExploreHeader from '../components/explore/ExploreHeader';
import ExploreFooter from '../components/explore/ExploreFooter';
import ExploreCta from '../components/explore/ExploreCta';
import '../styles/home-page.css';
import '../styles/explore-page.css';

const featureIcons = [FaHeadphones, FaPercent, FaShieldAlt, FaCar];
const SERVICE_ICONS = { hotels: FaBed, cars: FaCar, packages: FaSuitcase, cruises: FaShip };

function OfferCard({ image, title, subtitle, price, priceSuffix, onBook }) {
  return (
    <article className="mcr-offer-card">
      <div className="mcr-offer-card__media">
        <img src={image} alt="" loading="lazy" />
      </div>
      <div className="mcr-offer-card__body">
        <h3>{title}</h3>
        {subtitle && <p className="mcr-offer-card__meta">{subtitle}</p>}
        <div className="mcr-offer-card__foot">
          <p className="mcr-price-highlight">
            from <strong>${price}</strong>
            {priceSuffix && <span className="mcr-price-suffix">{priceSuffix}</span>}
          </p>
          <button type="button" className="mcr-btn-primary" onClick={onBook}>
            Book
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ExplorePage() {
  return (
    <div className="explore-page">
      <ExploreHeader />
      <main className="mcr-home">
        <div className="mcr-hero-split">
          <section className="mcr-hero" id="home" aria-label="Travel search hero">
            <div
              className="mcr-hero__bg"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.45) 0%, rgba(22, 163, 74, 0.18) 45%, rgba(15, 23, 42, 0.6) 100%), url(${HERO_IMAGE})`,
              }}
            />
            <div className="container mcr-hero__stack">
              <h1 className="mcr-hero__title">Hotels, cars, packages &amp; cruises — book online</h1>
              <p className="mcr-hero__lead">
                Search all travel options in one place with {SITE_NAME} concierge support.
              </p>
            </div>
          </section>

          <div className="mcr-hero-split__bridge">
            <div className="container mcr-hero-split__widget">
              <CarSearchWidget />
            </div>
          </div>

          <section className="mcr-section mcr-section--services mcr-section--compact">
            <div className="container">
              <SectionHeader
                eyebrow="What you can book"
                title="All travel options in one search"
                subtitle="Use the tabs above for hotels, cars, vacation packages, or cruises."
                align="center"
              />
              <div className="mcr-services-grid">
                {TRAVEL_SERVICES.map((s) => {
                  const Icon = SERVICE_ICONS[s.id];
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className="mcr-service-card"
                      onClick={() => scrollToTravelSection(s.id)}
                    >
                      <div className="mcr-service-card__img">
                        <img src={s.image} alt="" loading="lazy" />
                        <span className="mcr-service-card__icon">
                          <Icon aria-hidden />
                        </span>
                      </div>
                      <div className="mcr-service-card__body">
                        <h3>{s.title}</h3>
                        <p>{s.description}</p>
                        <span className="mcr-service-card__price">{s.priceHint}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <ExploreCta
                variant="compact"
                className="explore-cta-after-services"
                title="Not sure where to start?"
                subtitle="Call us anytime for help with any travel type."
              />
            </div>
          </section>
        </div>

        <section className="mcr-section mcr-section--compact" id="hotels">
          <div className="container">
            <SectionHeader
              eyebrow="Hotels"
              title="Top hotel destinations"
              subtitle="Compare nightly rates, check-in dates, and guest options."
            />
            <div className="mcr-offers-grid">
              {HOTEL_PICKS.map((h) => (
                <OfferCard
                  key={h.id}
                  image={h.image}
                  title={h.city}
                  subtitle={h.country}
                  price={h.price}
                  priceSuffix="/night"
                  onBook={() => scrollToTravelSection('enquiry')}
                />
              ))}
            </div>
            <ExploreCta
              variant="compact"
              title="Book a hotel with concierge support"
              subtitle="We compare rates and handle changes for you."
            />
          </div>
        </section>

        <section className="mcr-section mcr-section--soft mcr-section--compact" id="cars">
          <div className="container">
            <SectionHeader
              eyebrow="Cars"
              title="Popular car rental hubs"
              subtitle="Pick-up and drop-off at airports and city locations."
            />
            <div className="mcr-offers-grid">
              {TOP_LOCATIONS.map((d) => (
                <OfferCard
                  key={d.id}
                  image={d.image}
                  title={d.city}
                  subtitle={d.country}
                  price={d.price}
                  priceSuffix="/day"
                  onBook={() => scrollToTravelSection('enquiry')}
                />
              ))}
            </div>
            <ExploreCta
              variant="compact"
              title="Reserve your rental car"
              subtitle="Airport, hotel, or custom pickup — we’ll find the best rate."
            />
          </div>
        </section>

        <section className="mcr-section mcr-section--compact" id="packages">
          <div className="container">
            <SectionHeader
              eyebrow="Packages"
              title="Vacation package deals"
              subtitle="Bundled getaways with depart and return dates — scroll to see more."
            />
            <div className="mcr-packages-scroll" role="region" aria-label="Vacation package deals">
              <div className="mcr-packages-scroll__track">
                {PACKAGE_DEALS.map((p) => (
                  <div key={p.id} className="mcr-packages-scroll__item">
                    <OfferCard
                      image={p.image}
                      title={p.destination}
                      subtitle={`${p.details} · ${p.dates}`}
                      price={p.price}
                      priceSuffix="/person"
                      onBook={() => scrollToTravelSection('enquiry')}
                    />
                  </div>
                ))}
              </div>
            </div>
            <ExploreCta
              variant="compact"
              title="Plan your vacation package"
              subtitle="Flight + hotel bundles with transparent pricing."
            />
          </div>
        </section>

        <section className="mcr-section mcr-section--soft mcr-section--compact" id="cruises">
          <div className="container">
            <SectionHeader
              eyebrow="Cruises"
              title="Cruise sailings by region"
              subtitle="Departure ports, regions, and guest counts — search above to compare."
            />
            <div className="mcr-offers-grid">
              {CRUISE_DEALS.map((c) => (
                <OfferCard
                  key={c.id}
                  image={c.image}
                  title={`${c.region} cruise`}
                  subtitle={`${c.port} · ${c.nights} nights · ${c.dates}`}
                  price={c.price}
                  priceSuffix="/person"
                  onBook={() => scrollToTravelSection('enquiry')}
                />
              ))}
            </div>
            <ExploreCta
              variant="compact"
              title="Find your perfect cruise"
              subtitle="Caribbean, Mediterranean, Alaska & more — expert cabin advice."
            />
            <p className="explore-cruise-link">
              <Link to="/cruises">Browse dedicated cruise search →</Link>
            </p>
          </div>
        </section>

        <section className="mcr-section mcr-section--dark mcr-section--compact">
          <div className="container">
            <SectionHeader
              eyebrow="Why us"
              title={`Why travelers choose ${SITE_NAME}`}
              subtitle="One team for hotels, rentals, packages, and cruises."
              light
            />
            <div className="mcr-features">
              {FEATURES.map((f, i) => {
                const Icon = featureIcons[i];
                return (
                  <article key={f.title} className="mcr-feature">
                    <div className="mcr-feature__icon">
                      <Icon size={20} />
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </article>
                );
              })}
            </div>
            <ExploreCta
              variant="dark"
              title="Ready to book your trip?"
              subtitle="Hotels, cars, packages & cruises — one team, one phone call."
            />
          </div>
        </section>

        <section className="mcr-section mcr-section--compact">
          <div className="container">
            <SectionHeader
              eyebrow="Featured"
              title="Deals across every category"
              subtitle="Hotel, car, package, and cruise offers updated regularly."
            />
            <div className="mcr-deals-grid mcr-deals-grid--travel">
              {EXCLUSIVE_DEALS.map((deal) => (
                <article key={deal.id} className="mcr-deal-card mcr-deal-card--tagged">
                  <span className="mcr-deal-card__tag">{deal.category}</span>
                  <div className="mcr-deal-card__img">
                    <img src={deal.image} alt={deal.label} loading="lazy" />
                  </div>
                  <div className="mcr-deal-card__body">
                    <p className="mcr-deal-card__route">{deal.label}</p>
                    <p className="mcr-deal-meta">{deal.dates}</p>
                    <div className="mcr-deal-card__foot">
                      <p className="mcr-price-highlight">${deal.price}</p>
                      <button
                        type="button"
                        className="mcr-btn-primary"
                        onClick={() => scrollToTravelSection('enquiry')}
                      >
                        Book
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <ExploreCta
              variant="compact"
              title="Lock in a featured deal"
              subtitle="Limited-time offers — call our concierge today."
            />
          </div>
        </section>

        <section className="mcr-section mcr-section--reviews mcr-section--compact">
          <div className="container">
            <SectionHeader
              title="Trusted by travelers"
              subtitle="Reviews from hotel, car, package, and cruise bookings."
              align="center"
            />
            <div className="mcr-reviews-grid">
              {REVIEWS.map((r) => (
                <article key={r.title} className="mcr-review-card">
                  <div className="mcr-stars" aria-hidden>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <FaStar key={s} size={12} />
                    ))}
                  </div>
                  <h3>&ldquo;{r.title}&rdquo;</h3>
                  <p>{r.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mcr-section mcr-section--compact explore-cta-section">
          <div className="container">
            <ExploreCta
              variant="promo"
              title="Speak with a travel concierge today"
              subtitle="Tell us your dates and destination — we’re available 24/7."
            />
          </div>
        </section>

        <div className="explore-enquiry-wrap" id="enquiry">
          <EnquiryForm />
        </div>
      </main>
      <ExploreFooter />
    </div>
  );
}
